import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchPublicBranding, applyBranding, logoSrcOnDark, KOMERCES_DEFAULT } from '../utils/branding';

// Mélange une couleur hex vers une cible (ratio 0→1 = part de la cible).
const _hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const mixColor = (hex, target, ratio) => {
  const a = _hexToRgb(hex);
  const b = _hexToRgb(target);
  if (!a || !b) return hex;
  const m = a.map((v, i) => Math.round(v * (1 - ratio) + b[i] * ratio));
  return `rgb(${m[0]}, ${m[1]}, ${m[2]})`;
};

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(KOMERCES_DEFAULT);
  const [searchParams] = useSearchParams();
  // Bannière explicative après une déconnexion automatique (session expirée / ouverte ailleurs)
  const sessionExpired = searchParams.get('reason') === 'session';

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Branding dynamique par host (ou aperçu Super Admin via ?preview=agence_id)
  useEffect(() => {
    const previewId = searchParams.get('preview');
    const previewLevel = searchParams.get('preview_level');
    fetchPublicBranding(window.location.host, previewId, previewLevel)
      .then((b) => {
        setBranding(b);
        applyBranding(b);
      })
      .catch(() => { /* fallback Komerces déjà géré */ });
  }, [searchParams]);

  const isBranded = branding && !branding.is_komerces_default;
  const primary = branding?.couleur_primaire || '#007BFF';
  const displayName = branding?.nom_affiche || 'Komerces';

  // Fond white-label : dérivé de LA couleur primaire de l'agence (assombrie pour rester
  // lisible avec la carte en verre + texte clair + logo version fond foncé).
  const DEEP = '#070b16';
  const brandedBg = {
    background: `linear-gradient(160deg, ${mixColor(primary, DEEP, 0.40)} 0%, ${mixColor(primary, DEEP, 0.70)} 50%, ${DEEP} 100%)`,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Purge tout token/état résiduel AVANT d'authentifier (session précédente, autre compte).
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (_) { /* noop */ }
    window.__authRedirectInProgress = false;

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // trim : les espaces parasites (autocomplétion mobile, copier-coller) faisaient
        // échouer le login avec de « bons identifiants ».
        body: JSON.stringify({ email: email.trim(), password })
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_) { /* réponse non-JSON (proxy/504…) */ }

      if (response.ok && data) {
        // Purge tout résidu du compte précédent (branding/tenant) AVANT d'installer la nouvelle session.
        localStorage.removeItem('agence_branding');
        localStorage.setItem('token', data.token);
        // ⚠️ Fix iter91 : réinitialiser l'horloge d'activité partagée cross-tabs.
        // Sans ceci, un timestamp obsolète hérité d'une session précédente (typiquement quand la session
        // a été fermée à distance par un autre appareil, i.e. sans passer par clearAuth()) déclencherait
        // la modale « Vous allez être déconnecté » immédiatement après login.
        try {
          const now = Date.now();
          localStorage.setItem('komerces_last_activity', String(now));
          // Propager aux autres onglets pour qu'ils ne soient pas eux non plus en état d'inactivité fictive.
          try {
            if (typeof BroadcastChannel !== 'undefined') {
              const bc = new BroadcastChannel('komerces-idle');
              bc.postMessage({ type: 'activity', ts: now });
              bc.close();
            }
          } catch { /* BroadcastChannel non supporté : le storage event fera le fallback */ }
        } catch { /* localStorage indispo (Safari privé) : le manager basculera en mémoire */ }
        // Propage le flag de réinitialisation forcée dans l'objet user stocké.
        const storedUser = { ...data.user, force_password_reset_at_next_login: !!data.force_password_reset };
        localStorage.setItem('user', JSON.stringify(storedUser));

        // Drapeau pour le toast "une autre session a été fermée" (lu après redirection)
        if (data.previous_session_terminated) {
          sessionStorage.setItem('komerces_prev_session_terminated', '1');
        }

        if (onLogin) {
          onLogin(storedUser);
        }

        // Cible de redirection selon le rôle / la réinitialisation forcée.
        let target = '/';
        if (data.force_password_reset) {
          target = '/force-reset-password';
        } else if (data.user.role === 'super_admin') {
          target = '/super-admin';
        }
        // Rechargement COMPLET (pas de navigate SPA) : garantit qu'aucun état React
        // d'un compte précédent ne subsiste (évite le flash "infos d'un autre utilisateur").
        window.location.replace(target);
        return;
      } else {
        // Message précis selon le statut : ne jamais afficher « identifiants incorrects »
        // pour un problème serveur ou un service en cours de redémarrage.
        if (response.status === 401 || response.status === 403) {
          setError(data?.detail || 'Email ou mot de passe incorrect');
        } else if (response.status === 429) {
          setError('Trop de tentatives, réessayez dans quelques instants.');
        } else {
          setError('Le serveur est momentanément indisponible, réessayez dans un instant.');
        }
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen ${isBranded ? '' : 'bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]'} flex items-center justify-center p-4`}
      style={isBranded ? brandedBg : undefined}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo version FOND FONCÉ (logo_url_dark) si dispo, sinon fallback */}
        <div className="text-center mb-8">
          <img
            src={logoSrcOnDark(branding)}
            alt={displayName}
            className="h-16 w-auto mx-auto mb-6 drop-shadow-lg object-contain"
            data-testid="login-logo"
            onError={(e) => { e.currentTarget.src = '/logo_komerces_fonce.svg'; }}
          />
          <h1 className="text-2xl font-bold text-white" data-testid="login-title">Bienvenue sur {displayName}</h1>
          <p className="text-slate-300 mt-2 text-sm">
            {branding?.tagline || 'Connectez-vous à votre espace'}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {sessionExpired && !error && (
              <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 text-amber-100 text-sm flex items-start gap-2" data-testid="login-session-expired">
                <i className="bi bi-clock-history mt-0.5"></i>
                <span>
                  <strong>Session expirée</strong> — votre session a expiré après inactivité ou a été ouverte
                  sur un autre appareil. Reconnectez-vous pour reprendre votre travail.
                </span>
              </div>
            )}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-200 text-sm flex items-center gap-2" data-testid="login-error">
                <i className="bi bi-exclamation-triangle"></i>
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Email
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-300">
                  <i className="bi bi-envelope"></i>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-12 pr-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="votre@email.fr"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-300">
                  <i className="bi bi-lock"></i>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-12 pr-12 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-300 hover:text-white transition"
                  data-testid="login-password-toggle"
                >
                  <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"}></i>
                </button>
              </div>
              <div className="mt-2 text-right">
                <a
                  href="/forgot-password"
                  className="text-sm text-slate-300 hover:text-white underline-offset-2 hover:underline transition"
                  data-testid="login-forgot-password-link"
                >
                  Mot de passe oublié ?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={isBranded ? { backgroundColor: primary } : undefined}
              className={`w-full ${isBranded ? '' : 'bg-[#0062CC] hover:bg-[#0056b3]'} text-white font-semibold py-3 px-4 rounded-xl transition transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2`}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <i className="bi bi-arrow-repeat animate-spin"></i>
                  Connexion...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right"></i>
                  Se connecter
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-xs mt-8" data-testid="login-footer">
          {branding?.show_komerces_footer === false && branding?.footer_text
            ? branding.footer_text
            : (isBranded ? 'Propulsé par Komerces' : '© 2026 Komerces — Tous droits réservés')}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
